import React, { useState, useRef, useCallback, useEffect } from 'react';
import { 
  CheckCircle, XCircle, Star, Trophy, ChevronRight, RotateCcw,
  Pencil, Search, ArrowRight, Loader2, Eraser, Palette
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const COLORS = ['#ef4444','#f97316','#eab308','#22c55e','#3b82f6','#8b5cf6','#ec4899','#000000','#ffffff'];

// ==================== QUESTION RENDERERS ====================

const MultipleChoice = ({ question, answer, onAnswer, showResult }) => (
  <div className="space-y-2" data-testid={`q-${question.id}`}>
    <p className="text-white font-medium text-base mb-3">{question.question}</p>
    <div className="grid grid-cols-1 gap-2">
      {question.options?.map((opt, i) => {
        const isSelected = answer === opt;
        const isCorrect = showResult && opt === question.correct;
        const isWrong = showResult && isSelected && opt !== question.correct;
        return (
          <button key={i} onClick={() => !showResult && onAnswer(opt)}
            disabled={showResult}
            data-testid={`option-${question.id}-${i}`}
            className={`w-full text-left p-3 rounded-lg border transition-all text-sm
              ${isCorrect ? 'border-emerald-400 bg-emerald-500/20 text-emerald-300' : ''}
              ${isWrong ? 'border-red-400 bg-red-500/20 text-red-300' : ''}
              ${isSelected && !showResult ? 'border-indigo-400 bg-indigo-500/20 text-white' : ''}
              ${!isSelected && !isCorrect && !isWrong ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-indigo-400 hover:bg-indigo-500/10' : ''}
            `}>
            <span className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ borderColor: isCorrect ? '#34d399' : isWrong ? '#f87171' : isSelected ? '#818cf8' : '#475569' }}>
                {String.fromCharCode(65 + i)}
              </span>
              {opt}
              {isCorrect && <CheckCircle className="w-4 h-4 text-emerald-400 ml-auto flex-shrink-0" />}
              {isWrong && <XCircle className="w-4 h-4 text-red-400 ml-auto flex-shrink-0" />}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

const TrueFalse = ({ question, answer, onAnswer, showResult }) => (
  <div className="space-y-3" data-testid={`q-${question.id}`}>
    <p className="text-white font-medium text-base mb-3">{question.question}</p>
    <div className="grid grid-cols-2 gap-3">
      {[true, false].map(val => {
        const isSelected = answer === val;
        const isCorrect = showResult && val === question.correct;
        const isWrong = showResult && isSelected && val !== question.correct;
        return (
          <button key={String(val)} onClick={() => !showResult && onAnswer(val)}
            disabled={showResult}
            data-testid={`tf-${question.id}-${val}`}
            className={`p-4 rounded-lg border text-center font-bold text-lg transition-all
              ${isCorrect ? 'border-emerald-400 bg-emerald-500/20 text-emerald-300' : ''}
              ${isWrong ? 'border-red-400 bg-red-500/20 text-red-300' : ''}
              ${isSelected && !showResult ? 'border-indigo-400 bg-indigo-500/20 text-white' : ''}
              ${!isSelected && !isCorrect && !isWrong ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-indigo-400' : ''}
            `}>
            {val ? 'True' : 'False'}
            {isCorrect && <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto mt-1" />}
          </button>
        );
      })}
    </div>
    {showResult && question.explanation && (
      <p className="text-sm text-indigo-300 bg-indigo-500/10 rounded-lg p-3 mt-2">{question.explanation}</p>
    )}
  </div>
);

const FillBlank = ({ question, answer, onAnswer, showResult }) => (
  <div className="space-y-3" data-testid={`q-${question.id}`}>
    <p className="text-white font-medium text-base">{question.question}</p>
    <p className="text-slate-300 text-sm italic bg-slate-800/50 p-3 rounded-lg">{question.display_text}</p>
    <div className="flex items-center gap-2">
      <input type="text" value={answer || ''} onChange={e => !showResult && onAnswer(e.target.value)}
        disabled={showResult} placeholder="Type your answer..."
        data-testid={`input-${question.id}`}
        className="flex-1 bg-slate-800 border border-slate-600 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-indigo-400 focus:outline-none disabled:opacity-50" />
    </div>
    {question.hint && !showResult && !answer && (
      <p className="text-xs text-amber-400/70">Hint: {question.hint}</p>
    )}
    {showResult && (
      <div className={`flex items-center gap-2 text-sm p-2 rounded-lg ${answer?.toLowerCase().trim() === question.correct ? 'text-emerald-300 bg-emerald-500/10' : 'text-red-300 bg-red-500/10'}`}>
        {answer?.toLowerCase().trim() === question.correct 
          ? <><CheckCircle className="w-4 h-4" /> Correct!</>
          : <><XCircle className="w-4 h-4" /> The answer is: <strong>{question.correct}</strong></>}
      </div>
    )}
  </div>
);

const MatchingGame = ({ question, answer, onAnswer, showResult }) => {
  const [selected, setSelected] = useState(null);
  const matches = answer || {};

  const handleLeftClick = (left) => {
    if (showResult) return;
    setSelected(left);
  };

  const handleRightClick = (right) => {
    if (showResult || !selected) return;
    const newMatches = { ...matches, [selected]: right };
    onAnswer(newMatches);
    setSelected(null);
  };

  return (
    <div className="space-y-3" data-testid={`q-${question.id}`}>
      <p className="text-white font-medium text-base mb-1">{question.question}</p>
      <p className="text-xs text-slate-400 mb-2">Click a word on the left, then click its match on the right</p>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          {question.pairs?.map((p, i) => {
            const isMatched = matches[p.left] !== undefined;
            const isActive = selected === p.left;
            return (
              <button key={i} onClick={() => handleLeftClick(p.left)}
                className={`w-full p-2.5 rounded-lg border text-sm text-left transition-all
                  ${isActive ? 'border-amber-400 bg-amber-500/20 text-amber-300' : ''}
                  ${isMatched && !isActive ? 'border-indigo-400 bg-indigo-500/10 text-indigo-300' : ''}
                  ${!isMatched && !isActive ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-amber-400' : ''}
                `}>
                {p.left}
              </button>
            );
          })}
        </div>
        <div className="space-y-2">
          {question.pairs?.map((p, i) => {
            const isUsed = Object.values(matches).includes(p.right);
            const correctFor = showResult ? question.pairs.find(pair => pair.right === p.right)?.left : null;
            const matchedLeft = Object.entries(matches).find(([, v]) => v === p.right)?.[0];
            const isCorrect = showResult && correctFor === matchedLeft;
            return (
              <button key={i} onClick={() => handleRightClick(p.right)}
                disabled={isUsed || showResult}
                className={`w-full p-2.5 rounded-lg border text-sm text-left transition-all
                  ${isCorrect ? 'border-emerald-400 bg-emerald-500/10 text-emerald-300' : ''}
                  ${showResult && !isCorrect && isUsed ? 'border-red-400 bg-red-500/10 text-red-300' : ''}
                  ${isUsed && !showResult ? 'border-indigo-400 bg-indigo-500/10 text-indigo-300 opacity-60' : ''}
                  ${!isUsed && !showResult ? 'border-slate-600 bg-slate-800/50 text-slate-300 hover:border-indigo-400' : ''}
                `}>
                {p.right}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const WordSearchGame = ({ question, answer, onAnswer, showResult }) => {
  const [foundWords, setFoundWords] = useState(answer || []);
  const [selecting, setSelecting] = useState([]);
  const grid = question.grid || [];

  const toggleCell = (r, c) => {
    if (showResult) return;
    const key = `${r}-${c}`;
    setSelecting(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const checkWord = () => {
    const selected = selecting.map(k => {
      const [r, c] = k.split('-').map(Number);
      return grid[r]?.[c] || '';
    }).join('');
    const reversed = selected.split('').reverse().join('');
    const match = question.words.find(w => w.toUpperCase() === selected || w.toUpperCase() === reversed);
    if (match && !foundWords.includes(match.toUpperCase())) {
      const updated = [...foundWords, match.toUpperCase()];
      setFoundWords(updated);
      onAnswer(updated);
      toast.success(`Found: ${match}!`);
    }
    setSelecting([]);
  };

  return (
    <div className="space-y-3" data-testid={`q-${question.id}`}>
      <p className="text-white font-medium text-base">{question.question}</p>
      <div className="flex flex-wrap gap-2 mb-2">
        {question.words?.map((w, i) => (
          <span key={i} className={`px-2 py-1 rounded text-xs font-mono ${foundWords.includes(w.toUpperCase()) ? 'bg-emerald-500/30 text-emerald-300 line-through' : 'bg-slate-700 text-slate-300'}`}>
            {w}
          </span>
        ))}
      </div>
      <div className="bg-slate-800/80 rounded-lg p-2 inline-block">
        <div className="grid gap-0.5" style={{ gridTemplateColumns: `repeat(${grid[0]?.length || 10}, 1fr)` }}>
          {grid.map((row, r) => row.map((cell, c) => {
            const key = `${r}-${c}`;
            const isSelected = selecting.includes(key);
            return (
              <button key={key} onClick={() => toggleCell(r, c)}
                className={`w-7 h-7 text-xs font-mono font-bold rounded flex items-center justify-center transition-all
                  ${isSelected ? 'bg-amber-500 text-white' : 'bg-slate-700/50 text-slate-300 hover:bg-slate-600'}`}>
                {cell}
              </button>
            );
          }))}
        </div>
      </div>
      {selecting.length > 0 && (
        <Button size="sm" onClick={checkWord} className="bg-amber-500 hover:bg-amber-600 text-white">
          <Search className="w-3 h-3 mr-1" /> Check Word
        </Button>
      )}
      <p className="text-xs text-slate-500">Found {foundWords.length}/{question.words?.length || 0} words</p>
    </div>
  );
};

const DrawingCanvas = ({ question, answer, onAnswer }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [color, setColor] = useState('#3b82f6');
  const [brushSize, setBrushSize] = useState(3);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = useCallback((e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches?.[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const startDraw = useCallback((e) => {
    e.preventDefault();
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }, [getPos]);

  const draw = useCallback((e) => {
    if (!drawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.stroke();
  }, [drawing, color, brushSize, getPos]);

  const endDraw = useCallback(() => {
    setDrawing(false);
    if (canvasRef.current) onAnswer('drawing_completed');
  }, [onAnswer]);

  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  return (
    <div className="space-y-3" data-testid={`q-${question.id}`}>
      <p className="text-white font-medium text-base">{question.question}</p>
      <p className="text-sm text-slate-400">{question.prompt}</p>
      <div className="flex items-center gap-2 flex-wrap">
        {COLORS.map(c => (
          <button key={c} onClick={() => setColor(c)}
            className={`w-6 h-6 rounded-full border-2 transition-transform ${color === c ? 'border-white scale-125' : 'border-transparent'}`}
            style={{ backgroundColor: c }} />
        ))}
        <div className="ml-2 flex items-center gap-1">
          <Pencil className="w-3 h-3 text-slate-400" />
          <input type="range" min="1" max="10" value={brushSize} onChange={e => setBrushSize(Number(e.target.value))}
            className="w-16 h-1 accent-indigo-500" />
        </div>
        <Button size="sm" variant="ghost" onClick={clearCanvas} className="text-slate-400 hover:text-white ml-2">
          <Eraser className="w-3 h-3 mr-1" /> Clear
        </Button>
      </div>
      <canvas ref={canvasRef} width={340} height={220}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
        className="rounded-lg border border-slate-600 cursor-crosshair w-full max-w-[340px] touch-none" />
      {answer && <p className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Drawing saved!</p>}
    </div>
  );
};


// ==================== MAIN COMPONENT ====================

const InteractiveWorksheet = ({ weekNumber, childId, onComplete, api }) => {
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    fetchWorksheet();
  }, [weekNumber]);

  const fetchWorksheet = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await api.get(`/christian/worksheet/${weekNumber}/interactive?child_id=${childId || ''}&include_ai=true`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setWorksheet(r.data);
      setAnswers({});
      setCurrentQ(0);
      setResults(null);
      setShowResult(false);
    } catch (err) {
      toast.error('Failed to load worksheet');
    } finally {
      setLoading(false);
    }
  };

  const setAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');
      const r = await api.post(`/christian/worksheet/${weekNumber}/submit`, {
        child_id: childId,
        answers
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setResults(r.data);
      setShowResult(true);
      setCurrentQ(0);
      if (r.data.score >= 70) {
        toast.success(`Score: ${r.data.score}% - ${r.data.message}`);
      } else {
        toast('Keep practicing!', { icon: '💪' });
      }
    } catch (err) {
      toast.error('Failed to submit worksheet');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      <span className="ml-3 text-slate-400">Loading worksheet...</span>
    </div>
  );

  if (!worksheet) return null;

  const questions = worksheet.questions || [];
  const currentQuestion = questions[currentQ];
  const answeredCount = Object.keys(answers).length;
  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  // Results view
  if (results) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="space-y-4">
        <Card className="bg-gradient-to-br from-amber-900/40 to-orange-900/30 border-amber-500/30 text-center">
          <CardContent className="p-6">
            <div className="text-5xl mb-3">{results.score >= 90 ? '🌟' : results.score >= 70 ? '🎉' : results.score >= 50 ? '👍' : '💪'}</div>
            <h2 className="text-2xl font-bold text-white mb-1">Worksheet Complete!</h2>
            <p className="text-slate-300 text-sm mb-4">{results.message}</p>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-2xl font-bold text-amber-400">{results.score}%</p>
                <p className="text-xs text-slate-400">Score</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-2xl font-bold text-emerald-400">+{results.xp_earned}</p>
                <p className="text-xs text-slate-400">XP Earned</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-3">
                <p className="text-2xl font-bold text-indigo-400">{results.points_earned}/{results.points_possible}</p>
                <p className="text-xs text-slate-400">Points</p>
              </div>
            </div>
            {results.badges_earned?.length > 0 && (
              <div className="bg-amber-500/20 border border-amber-500/30 rounded-xl p-3 mb-4">
                <p className="text-amber-300 font-medium text-sm mb-1">Badge Earned!</p>
                {results.badges_earned.map((b, i) => (
                  <p key={i} className="text-amber-200 font-bold">{b.name}</p>
                ))}
              </div>
            )}
            <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
              {results.results?.map((r, i) => (
                <div key={i} className={`flex items-center justify-between p-2 rounded-lg text-xs ${r.correct ? 'bg-emerald-500/10 text-emerald-300' : 'bg-red-500/10 text-red-300'}`}>
                  <span className="flex items-center gap-1">
                    {r.correct ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    Q{i + 1} ({r.type})
                  </span>
                  <span>+{r.earned} pts</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchWorksheet} variant="outline" className="flex-1 border-slate-600 text-white">
                <RotateCcw className="w-4 h-4 mr-1" /> Try Again
              </Button>
              <Button onClick={() => onComplete?.(results)} className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600">
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const renderQuestion = (q) => {
    const props = { question: q, answer: answers[q.id], onAnswer: (v) => setAnswer(q.id, v), showResult };
    switch (q.type) {
      case 'multiple_choice': return <MultipleChoice {...props} />;
      case 'true_false': return <TrueFalse {...props} />;
      case 'fill_blank': return <FillBlank {...props} />;
      case 'matching': return <MatchingGame {...props} />;
      case 'word_search': return <WordSearchGame {...props} />;
      case 'drawing': return <DrawingCanvas {...props} />;
      default: return <MultipleChoice {...props} />;
    }
  };

  const questionTypeLabel = {
    'multiple_choice': 'Multiple Choice',
    'true_false': 'True or False',
    'fill_blank': 'Fill in the Blank',
    'matching': 'Matching',
    'word_search': 'Word Search',
    'drawing': 'Drawing Time',
  };

  return (
    <div className="space-y-4" data-testid="interactive-worksheet">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold text-lg flex items-center gap-2">
            <Palette className="w-5 h-5 text-pink-400" />
            Interactive Worksheet
          </h3>
          <p className="text-slate-400 text-xs">Week {weekNumber}: {worksheet.topic}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Question {currentQ + 1} of {questions.length}</p>
          <p className="text-xs text-amber-400">{worksheet.total_points} points possible</p>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-1">
        <Progress value={((currentQ + 1) / questions.length) * 100} className="h-2 bg-slate-700" />
        <div className="flex justify-between text-[10px] text-slate-500">
          <span>{answeredCount} answered</span>
          <span>{questions.length - answeredCount} remaining</span>
        </div>
      </div>

      {/* Question Card */}
      {currentQuestion && (
        <AnimatePresence mode="wait">
          <motion.div key={currentQ}
            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.2 }}>
            <Card className="bg-slate-800/60 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-indigo-400 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <span className="w-5 h-5 bg-indigo-500/20 rounded-full flex items-center justify-center text-[10px] font-bold">{currentQ + 1}</span>
                    {questionTypeLabel[currentQuestion.type] || currentQuestion.type}
                  </span>
                  <span className="text-amber-400 text-xs">{currentQuestion.points} pts</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderQuestion(currentQuestion)}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Navigation */}
      <div className="flex items-center gap-2">
        {currentQ > 0 && (
          <Button variant="outline" onClick={() => setCurrentQ(currentQ - 1)} className="border-slate-600 text-white" size="sm">
            Back
          </Button>
        )}
        <div className="flex-1" />
        {currentQ < questions.length - 1 ? (
          <Button onClick={() => setCurrentQ(currentQ + 1)} className="bg-indigo-600 hover:bg-indigo-700" size="sm">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={submitting || answeredCount === 0}
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700" size="sm"
            data-testid="submit-worksheet-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trophy className="w-4 h-4 mr-1" />}
            Submit Worksheet
          </Button>
        )}
      </div>

      {/* Question dots */}
      <div className="flex items-center justify-center gap-1 flex-wrap">
        {questions.map((q, i) => (
          <button key={i} onClick={() => setCurrentQ(i)}
            className={`w-6 h-6 rounded-full text-[9px] font-bold transition-all
              ${i === currentQ ? 'bg-indigo-500 text-white ring-2 ring-indigo-300' : ''}
              ${answers[q.id] !== undefined && i !== currentQ ? 'bg-emerald-500/30 text-emerald-300' : ''}
              ${answers[q.id] === undefined && i !== currentQ ? 'bg-slate-700 text-slate-500' : ''}
            `}>
            {i + 1}
          </button>
        ))}
      </div>

      {worksheet.previously_completed && (
        <p className="text-center text-xs text-amber-400/60">
          Previously completed with {worksheet.previous_score}% score (+{worksheet.previous_xp} XP)
        </p>
      )}
    </div>
  );
};

export default InteractiveWorksheet;
